import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// SETUP COLORS
const PRIMARY = {
  lighter: '#AFC3D7',
  light: '#7D98B6',
  main: '#153D65',
  dark: '#14284E',
  darker: '#141E38',
  500_8: alpha('#153D65', 0.08),
  500_12: alpha('#153D65', 0.12),
  500_16: alpha('#153D65', 0.16),
  500_24: alpha('#153D65', 0.24),
  500_32: alpha('#153D65', 0.32),
  500_48: alpha('#153D65', 0.48),
  500_56: alpha('#153D65', 0.56),
  500_80: alpha('#153D65', 0.8),
};
const SECONDARY = {
  lighter: '#FBE3DE',
  light: '#F9D3CE',
  main: '#F5B6AC',
  dark: '#E38984',
  darker: '#C3646B',
  500_8: alpha('#F5B6AC', 0.08),
  500_12: alpha('#F5B6AC', 0.12),
  500_16: alpha('#F5B6AC', 0.16),
  500_24: alpha('#F5B6AC', 0.24),
  500_32: alpha('#F5B6AC', 0.32),
  500_48: alpha('#F5B6AC', 0.48),
  500_56: alpha('#F5B6AC', 0.56),
  500_80: alpha('#F5B6AC', 0.8),
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  500_8: alpha('#1890FF', 0.08),
  500_12: alpha('#1890FF', 0.12),
  500_16: alpha('#1890FF', 0.16),
  500_24: alpha('#1890FF', 0.24),
  500_32: alpha('#1890FF', 0.32),
  500_48: alpha('#1890FF', 0.48),
  500_56: alpha('#1890FF', 0.56),
  500_80: alpha('#1890FF', 0.8),
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#67CC5D',
  dark: '#3E7657',
  darker: '#155E57',
  500_8: alpha('#67CC5D', 0.08),
  500_12: alpha('#67CC5D', 0.12),
  500_16: alpha('#67CC5D', 0.16),
  500_24: alpha('#67CC5D', 0.24),
  500_32: alpha('#67CC5D', 0.32),
  500_48: alpha('#67CC5D', 0.48),
  500_56: alpha('#67CC5D', 0.56),
  500_80: alpha('#67CC5D', 0.8),
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  500_8: alpha('#FFC107', 0.08),
  500_12: alpha('#FFC107', 0.12),
  500_16: alpha('#FFC107', 0.16),
  500_24: alpha('#FFC107', 0.24),
  500_32: alpha('#FFC107', 0.32),
  500_48: alpha('#FFC107', 0.48),
  500_56: alpha('#FFC107', 0.56),
  500_80: alpha('#FFC107', 0.8),
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  500_8: alpha('#FF4842', 0.08),
  500_12: alpha('#FF4842', 0.12),
  500_16: alpha('#FF4842', 0.16),
  500_24: alpha('#FF4842', 0.24),
  500_32: alpha('#FF4842', 0.32),
  500_48: alpha('#FF4842', 0.48),
  500_56: alpha('#FF4842', 0.56),
  500_80: alpha('#FF4842', 0.8),
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: '#efe' },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  ...COMMON,
  text: {
    primary: PRIMARY.main,
    secondary: GREY[600],
    disabled: GREY[500],
  },
  background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
  action: { active: GREY[600], ...COMMON.action },
} as const;

export default palette;
